/* Sprint 2P — Driver PWA Route Map.
 *
 * Mobile-first map/navigation view for a single assigned route. Shows pickup
 * location, all stops in route order, a "Næste stop" highlight card, and one-tap
 * external navigation links (Google Maps / Apple Maps) per stop and for the
 * entire route as waypoints.
 *
 * Strictly view-only — no booking/customer revenue exposed.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  ArrowLeft,
  MapPin,
  Navigation,
  Phone,
  Truck,
  CheckCircle2,
  XCircle,
  Clock,
  Package,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function driverApi() {
  const token = localStorage.getItem("ema_driver_token");
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPad|iPhone|iPod/.test(ua);
};

function buildAddressString({ address, postal_code, city }) {
  return [address, [postal_code, city].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")
    .trim();
}

function googleMapsLink(stop) {
  if (stop.lat != null && stop.lng != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}&travelmode=driving`;
  }
  const q = encodeURIComponent(buildAddressString(stop));
  return `https://www.google.com/maps/dir/?api=1&destination=${q}&travelmode=driving`;
}

function appleMapsLink(stop) {
  if (stop.lat != null && stop.lng != null) {
    return `https://maps.apple.com/?daddr=${stop.lat},${stop.lng}&dirflg=d`;
  }
  const q = encodeURIComponent(buildAddressString(stop));
  return `https://maps.apple.com/?daddr=${q}&dirflg=d`;
}

function buildFullRouteGoogleLink(pickup, stops) {
  // Google Maps supports waypoints via "/" path separator: dir/origin/wp1/wp2/dest
  const points = [pickup, ...stops].filter((p) => p && (p.address || p.lat != null));
  if (points.length < 2) return null;
  const fmt = (p) => {
    if (p.lat != null && p.lng != null) return `${p.lat},${p.lng}`;
    return encodeURIComponent(buildAddressString(p));
  };
  const path = points.map(fmt).join("/");
  return `https://www.google.com/maps/dir/${path}`;
}

function StatusBadge({ state }) {
  const map = {
    delivered: {
      cls: "bg-green-100 text-green-800 border-green-200",
      label: "Leveret",
      Icon: CheckCircle2,
    },
    failed: {
      cls: "bg-red-100 text-red-800 border-red-200",
      label: "Ikke leveret",
      Icon: XCircle,
    },
    in_transit: {
      cls: "bg-blue-100 text-blue-800 border-blue-200",
      label: "Under transport",
      Icon: Truck,
    },
    pending: {
      cls: "bg-amber-100 text-amber-800 border-amber-200",
      label: "Afventer",
      Icon: Clock,
    },
  };
  const m = map[state] || map.pending;
  const I = m.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider border ${m.cls}`}
      data-testid={`stop-status-${state}`}
    >
      <I size={12} />
      {m.label}
    </span>
  );
}

function NavButtons({ stop, compact = false }) {
  const ios = isIOS();
  const primary = ios ? appleMapsLink(stop) : googleMapsLink(stop);
  const primaryLabel = ios ? "Apple Maps" : "Google Maps";
  const secondary = ios ? googleMapsLink(stop) : appleMapsLink(stop);
  const secondaryLabel = ios ? "Google Maps" : "Apple Maps";
  const size = compact ? "py-2 text-xs" : "py-3 text-sm";
  return (
    <div className="flex gap-2 mt-2">
      <a
        href={primary}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex-1 inline-flex items-center justify-center gap-1.5 bg-brand-red text-white font-bold ${size} uppercase tracking-wider`}
        data-testid={`nav-primary-${stop.id}`}
      >
        <Navigation size={14} /> {primaryLabel}
      </a>
      <a
        href={secondary}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex-1 inline-flex items-center justify-center gap-1.5 bg-brand-bg2 border border-brand text-brand-dark font-semibold ${size} uppercase tracking-wider`}
        data-testid={`nav-secondary-${stop.id}`}
      >
        <ExternalLink size={14} /> {secondaryLabel}
      </a>
    </div>
  );
}

export default function DriverRouteMap() {
  const { route_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await driverApi().get(`/driver/routes/${route_id}/stops`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        window.location.href = "/driver/login";
        return;
      }
      setError(e?.response?.data?.detail || "Kunne ikke hente rutekortet");
      toast.error(e?.response?.data?.detail || "Kunne ikke hente rutekortet");
    } finally {
      setLoading(false);
    }
  }, [route_id]);

  useEffect(() => {
    load();
  }, [load]);

  const nextStop = useMemo(() => {
    if (!data?.next_stop_id) return null;
    return data.stops.find((s) => s.id === data.next_stop_id) || null;
  }, [data]);

  const fullRouteLink = useMemo(() => {
    if (!data) return null;
    // Use pending + next stops as waypoints to keep within Google's URL limits
    const remaining = data.stops.filter((s) =>
      ["pending", "in_transit"].includes(s.state),
    );
    return buildFullRouteGoogleLink(data.pickup, remaining);
  }, [data]);

  if (loading) {
    return (
      <div
        className="min-h-screen bg-brand-bg flex items-center justify-center"
        data-testid="route-map-loading"
      >
        <Loader2 className="animate-spin text-brand-red" size={32} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-brand-bg" data-testid="route-map-error">
        <div className="bg-brand-dark text-white px-4 py-4 sticky top-0 z-10 flex items-center gap-3">
          <button
            onClick={() => navigate("/driver/my-routes")}
            className="text-white"
            data-testid="back-to-routes"
            aria-label="Tilbage"
          >
            <ArrowLeft size={22} />
          </button>
          <div className="font-display font-bold text-lg">Rutekort</div>
        </div>
        <div className="p-6 text-center bg-white border border-brand mx-4 mt-4">
          <div className="text-red-700 font-semibold mb-2">
            {error || "Rute ikke fundet"}
          </div>
          <button
            onClick={() => navigate("/driver/my-routes")}
            className="mt-2 px-4 py-2 bg-brand-red text-white text-sm font-bold uppercase tracking-wider"
            data-testid="back-button-error"
          >
            Tilbage til mine ruter
          </button>
        </div>
      </div>
    );
  }

  const { pickup, stops, totals, route_number, route_date, route_status } = data;

  return (
    <div className="min-h-screen bg-brand-bg pb-24" data-testid="driver-route-map">
      {/* Sticky header */}
      <div className="bg-brand-dark text-white px-4 py-3 sticky top-0 z-20 flex items-center gap-3">
        <button
          onClick={() => navigate("/driver/my-routes")}
          className="text-white/90 hover:text-white"
          data-testid="back-to-routes"
          aria-label="Tilbage"
        >
          <ArrowLeft size={22} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base truncate" data-testid="route-header-title">
            {route_number || `Rute ${route_id.slice(0, 8)}`}
          </div>
          <div className="text-xs text-white/60 truncate">
            {route_date} · {route_status || "planlagt"} ·{" "}
            <span className="text-green-300">{totals.delivered}/{totals.total} leveret</span>
          </div>
        </div>
      </div>

      {/* Totals bar */}
      <div
        className="bg-white border-b border-brand px-4 py-3 flex items-center justify-between text-sm"
        data-testid="route-totals-bar"
      >
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-green-700 font-semibold">
            <CheckCircle2 size={14} /> {totals.delivered}
          </span>
          <span className="flex items-center gap-1 text-amber-700 font-semibold">
            <Clock size={14} /> {totals.pending}
          </span>
          {totals.failed > 0 && (
            <span className="flex items-center gap-1 text-red-700 font-semibold">
              <XCircle size={14} /> {totals.failed}
            </span>
          )}
        </div>
        <span className="text-brand-text2 text-xs">
          {totals.total} stops i alt
        </span>
      </div>

      <div className="p-4 space-y-3">
        {/* Pickup card */}
        {(pickup.address || pickup.name) && (
          <div
            className="bg-brand-dark text-white p-4 border-l-4 border-brand-red"
            data-testid="pickup-card"
          >
            <div className="text-[10px] uppercase tracking-widest text-brand-red font-bold">
              Afhentning
            </div>
            <div className="font-display font-bold text-base mt-1">
              {pickup.name}
            </div>
            {pickup.address && (
              <div className="text-sm text-white/80 mt-1 flex items-start gap-1">
                <MapPin size={14} className="mt-0.5 shrink-0" />
                <span>
                  {pickup.address}
                  {(pickup.postal_code || pickup.city) && (
                    <>
                      <br />
                      {[pickup.postal_code, pickup.city].filter(Boolean).join(" ")}
                    </>
                  )}
                </span>
              </div>
            )}
            <NavButtons stop={{ ...pickup, id: "pickup" }} compact />
          </div>
        )}

        {/* Next stop highlight */}
        {nextStop && (
          <div
            className="bg-white border-2 border-brand-red p-4 shadow-md"
            data-testid="next-stop-card"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-brand-red font-bold">
                Næste stop · #{nextStop.sequence}
              </div>
              <StatusBadge state={nextStop.state} />
            </div>
            <div className="font-display font-bold text-lg text-brand-dark">
              {nextStop.recipient_name}
            </div>
            <div className="text-sm text-brand-text2 mt-1 flex items-start gap-1">
              <MapPin size={14} className="mt-0.5 shrink-0 text-brand-red" />
              <span>
                {nextStop.address}
                {(nextStop.postal_code || nextStop.city) && (
                  <>
                    <br />
                    {[nextStop.postal_code, nextStop.city].filter(Boolean).join(" ")}
                  </>
                )}
              </span>
            </div>
            {nextStop.delivery_notes && (
              <div className="mt-2 text-xs bg-amber-50 border-l-2 border-amber-500 p-2 text-amber-900">
                <b>Note:</b> {nextStop.delivery_notes}
              </div>
            )}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {nextStop.recipient_phone && (
                <a
                  href={`tel:${nextStop.recipient_phone}`}
                  className="inline-flex items-center gap-1 text-sm text-brand-dark border border-brand-dark px-3 py-2 font-semibold"
                  data-testid={`call-${nextStop.id}`}
                >
                  <Phone size={14} /> {nextStop.recipient_phone}
                </a>
              )}
              <span className="text-[10px] text-brand-text2 font-mono uppercase">
                {nextStop.label_number}
              </span>
            </div>
            <NavButtons stop={nextStop} />
          </div>
        )}

        {/* All stops list */}
        <div className="space-y-2" data-testid="stops-list">
          <div className="text-[10px] uppercase tracking-widest text-brand-text2 font-bold px-1">
            Alle stops i rækkefølge
          </div>
          {stops.length === 0 && (
            <div
              className="bg-white border border-brand p-6 text-center text-brand-text2"
              data-testid="no-stops"
            >
              Ingen stops registreret på denne rute.
            </div>
          )}
          {stops.map((s) => {
            const isNext = s.id === data.next_stop_id;
            return (
              <div
                key={s.id}
                className={`bg-white p-3 border ${
                  isNext ? "border-brand-red ring-1 ring-brand-red" : "border-brand"
                } ${s.state === "delivered" ? "opacity-70" : ""}`}
                data-testid={`stop-card-${s.id}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`shrink-0 w-9 h-9 flex items-center justify-center font-display font-bold text-sm ${
                      s.state === "delivered"
                        ? "bg-green-600 text-white"
                        : s.state === "failed"
                        ? "bg-red-600 text-white"
                        : isNext
                        ? "bg-brand-red text-white"
                        : "bg-brand-bg2 border border-brand text-brand-dark"
                    }`}
                    data-testid={`stop-seq-${s.id}`}
                  >
                    {s.state === "delivered" ? (
                      <CheckCircle2 size={18} />
                    ) : s.state === "failed" ? (
                      <XCircle size={18} />
                    ) : (
                      s.sequence
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-bold text-brand-dark text-sm truncate">
                        {s.recipient_name}
                      </div>
                      <StatusBadge state={s.state} />
                    </div>
                    <div className="text-xs text-brand-text2 mt-0.5 flex items-start gap-1">
                      <MapPin size={12} className="mt-0.5 shrink-0" />
                      <span>
                        {s.address}
                        {(s.postal_code || s.city) && (
                          <>, {[s.postal_code, s.city].filter(Boolean).join(" ")}</>
                        )}
                      </span>
                    </div>
                    {s.delivery_notes && (
                      <div className="mt-1 text-[11px] text-amber-900 bg-amber-50 border-l-2 border-amber-500 px-2 py-1">
                        {s.delivery_notes}
                      </div>
                    )}
                    {s.failure_reason && s.state === "failed" && (
                      <div className="mt-1 text-[11px] text-red-900 bg-red-50 border-l-2 border-red-500 px-2 py-1">
                        <b>Årsag:</b> {s.failure_reason}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[11px] text-brand-text2">
                      <Package size={12} />
                      <span className="font-mono">{s.label_number}</span>
                      {s.recipient_phone && (
                        <a
                          href={`tel:${s.recipient_phone}`}
                          className="text-brand-red font-semibold inline-flex items-center gap-1"
                          data-testid={`stop-call-${s.id}`}
                        >
                          <Phone size={12} /> {s.recipient_phone}
                        </a>
                      )}
                    </div>
                    {s.state !== "delivered" && <NavButtons stop={s} compact />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom sticky CTA: open full remaining route in Google Maps */}
      {fullRouteLink && (
        <div className="fixed bottom-0 inset-x-0 bg-brand-dark border-t border-white/10 p-3 z-20">
          <a
            href={fullRouteLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-red text-white font-bold py-3 text-sm uppercase tracking-wider"
            data-testid="open-full-route"
          >
            <Navigation size={16} /> Åbn hele ruten i Google Maps
          </a>
        </div>
      )}
    </div>
  );
}
